import React from 'react';
import { Box, Card, CardActionArea, CardMedia, Typography, Chip } from '@mui/material';
import { DirectionsCar as CarIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import FavoriteButton from './FavoriteButton';

function VehicleCard({ vehicle, sx }) {
  const navigate = useNavigate();
  const photo = vehicle.photo_url || vehicle.photo_urls?.[0] || null;
  const location = [vehicle.city, vehicle.state].filter(Boolean).join(', ');

  const handleClick = () => {
    navigate(`/vehicle/${vehicle.id}`);
  };

  return (
    <Card
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 6 },
        ...sx,
      }}
    >
      <CardActionArea onClick={handleClick}>
        {/* Image */}
        {photo ? (
          <CardMedia
            component="img"
            height="180"
            image={photo}
            alt={vehicle.heading || ''}
            sx={{ objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              height: 180,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.100',
            }}
          >
            <CarIcon sx={{ fontSize: 56, color: 'grey.400' }} />
          </Box>
        )}

        {/* Card body */}
        <Box sx={{ p: 1.5 }}>
          {vehicle.price > 0 && (
            <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
              ${vehicle.price.toLocaleString()}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {vehicle.year}
            {vehicle.mileage > 0 && ` \u00B7 ${vehicle.mileage.toLocaleString()} mi`}
            {vehicle.drivetrain && ` \u00B7 ${vehicle.drivetrain}`}
          </Typography>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {vehicle.make} {vehicle.model} {vehicle.trim}
          </Typography>
          {location && (
            <Typography variant="caption" color="text.secondary">
              {location}
            </Typography>
          )}
        </Box>
      </CardActionArea>

      {/* Favorite button — overlaid top-right */}
      <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 1 }}>
        <FavoriteButton vehicleId={vehicle.id} size="small" />
      </Box>

      {/* Badges — overlaid top-left */}
      <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1, display: 'flex', gap: 0.5 }}>
        {vehicle.is_certified === 1 && (
          <Chip label="CPO" size="small" color="info" sx={{ fontWeight: 'bold', fontSize: '0.65rem' }} />
        )}
        {vehicle.market_savings > 2000 && (
          <Chip
            label={`$${vehicle.market_savings.toLocaleString()} below market`}
            size="small"
            color="success"
            sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
          />
        )}
        {vehicle.dom != null && vehicle.dom >= 45 && (
          <Chip
            label={`Listed ${vehicle.dom} days`}
            size="small"
            sx={{ fontWeight: 'bold', fontSize: '0.65rem', bgcolor: 'rgba(0,0,0,0.6)', color: '#fff' }}
          />
        )}
      </Box>
    </Card>
  );
}

export default VehicleCard;
